// Le bouton « Connecter » de l'extension (issue #139) : Radar passe un code de
// connexion à l'extension dans l'onglet même, sans que personne le recopie.
//
// POURQUOI PAS `externally_connectable`. Radar devrait alors connaître
// l'identifiant de l'extension, qui change d'une installation non empaquetée à
// l'autre et ne se fixe que par une `key` au manifest, à poser avant la
// première publication sous peine de faire perdre leurs données aux
// installations existantes. Ici, c'est l'extension qui ouvre l'onglet Radar et
// y injecte un relais (`chrome.scripting`, sur l'hôte que l'utilisateur vient
// d'accorder) : Radar n'a rien à savoir d'elle, et le même code marche non
// empaqueté comme depuis le Chrome Web Store.
//
// LE PROTOCOLE, trois messages `window.postMessage` dans l'onglet Radar :
//  1. le relais DEMANDE, chaque seconde, tant qu'il n'a rien reçu (la page
//     peut être sur l'écran de connexion, ou pas encore montée) ;
//  2. la page, connectée, répond par un CODE, celui de
//     `POST /api/auth/extension/code` (dix minutes, une fois) ;
//  3. le relais rend le RÉSULTAT de l'échange, que la page affiche.
//
// Ce qui tient la sécurité : la page ne répond qu'à un message venu de sa
// propre fenêtre (`event.source === window`) et poste le code avec sa propre
// origine pour cible. Une page tierce qui aurait ouvert Radar en popup peut lui
// écrire, mais ni se faire répondre ni lire la réponse. Le code ne quitte donc
// l'onglet que par le relais de l'extension.
//
// Module pur, partagé par l'extension et Radar : les deux côtés lisent les
// mêmes noms de messages, sinon un renommage d'un seul côté couperait le pont
// sans aucune erreur.

export const PONT_DEMANDE = 'ajf-extension-demande'
export const PONT_CODE = 'ajf-extension-code'
export const PONT_RESULTAT = 'ajf-extension-resultat'

/** La page d'arrivée sur Radar : l'étape « brancher l'extension » de l'aide. */
export const PONT_ROUTE = '#/aide'

export type MessagePont =
  | { type: typeof PONT_DEMANDE }
  | { type: typeof PONT_CODE; code: string }
  | { type: typeof PONT_RESULTAT; ok: boolean }

/** Reconnaît un message du pont, et rien d'autre (les pages postent beaucoup). */
export function lireMessagePont(data: unknown): MessagePont | null {
  if (!data || typeof data !== 'object') return null
  const m = data as Record<string, unknown>
  if (m.type === PONT_DEMANDE) return { type: PONT_DEMANDE }
  if (m.type === PONT_CODE && typeof m.code === 'string' && m.code.length > 0) return { type: PONT_CODE, code: m.code }
  if (m.type === PONT_RESULTAT && typeof m.ok === 'boolean') return { type: PONT_RESULTAT, ok: m.ok }
  return null
}

// --- Le canal DIRECT, de Radar vers l'extension du store (issue #234) --------
//
// Le pont ci-dessus part de l'extension. Celui-ci part de RADAR : la page
// appelle `chrome.runtime.sendMessage(ID_EXTENSION, …)`, ce que le manifest
// ouvre à l'origine de Radar seulement (`externally_connectable`). Il sert à
// ce que Radar SACHE que l'extension est là, reprenne le CV qu'elle porte
// déjà, et la connecte d'un clic. Seule l'extension du Chrome Web Store
// répond : celle du zip a un autre identifiant, et Radar retombe alors sur le
// parcours d'avant, sans rien perdre.

export const EXT_PING = 'ajf-ext-ping'
export const EXT_CV = 'ajf-ext-cv'
export const EXT_CONNECTER = 'ajf-ext-connecter'

export type MessageExterne =
  | { type: typeof EXT_PING }
  | { type: typeof EXT_CV }
  | { type: typeof EXT_CONNECTER; code: string }

/** Ce que l'extension dit d'elle-même. `cv` : elle porte un CV LOCAL, que
 *  Radar peut reprendre (jamais vrai pour une extension déjà connectée, dont
 *  le CV est la copie de celui de Radar). */
export interface EtatExtensionExterne {
  version: string
  connectee: boolean
  cv: boolean
}

export type ReponseCvExterne = { ok: true; profile: unknown } | { ok: false }

export type ReponseConnexionExterne =
  | { ok: true }
  | { ok: false; raison: 'code' | 'sans-cv' | 'reseau' | 'autre' }

/** Reconnaît un message du canal direct, et rien d'autre. */
export function lireMessageExterne(data: unknown): MessageExterne | null {
  if (!data || typeof data !== 'object') return null
  const m = data as Record<string, unknown>
  if (m.type === EXT_PING) return { type: EXT_PING }
  if (m.type === EXT_CV) return { type: EXT_CV }
  if (m.type === EXT_CONNECTER && typeof m.code === 'string' && m.code.trim().length > 0) return { type: EXT_CONNECTER, code: m.code.trim() }
  return null
}
